import { type FC } from "react";
import {
  Box,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
} from "@mui/material";
import type { CustomerSummary } from "../customerSummariesApi";
import type { ClientDialogType } from "../useClientDialog";
import ClientsTableHead from "./ClientsTableHead";
import ClientRow from "./ClientRow";

interface Props {
  rows: CustomerSummary[];
  loading: boolean;
  allSelected: boolean;
  someSelected: boolean;
  isSelected: (id: string) => boolean;
  onToggleRow: (id: string) => void;
  onToggleAll: () => void;
  onOpenDialog: (type: ClientDialogType, client: CustomerSummary) => void;
}

const COLUMN_COUNT = 7;

const LoadingSpinner: FC = () => <CircularProgress aria-label="Ładowanie klientów" />;

/** Dims the rows already shown while a new filter result loads; the spinner sticks near the top of the viewport so it
 * stays visible on a long table. */
const LoadingOverlay: FC = () => (
  <Box
    sx={{
      position: "absolute",
      inset: 0,
      zIndex: 3,
      bgcolor: "rgba(255,255,255,0.6)",
      display: "flex",
      justifyContent: "center",
      alignItems: "flex-start",
    }}
  >
    <Box sx={{ position: "sticky", top: "40vh", pt: 8 }}>
      <LoadingSpinner />
    </Box>
  </Box>
);

const ClientsTable: FC<Props> = ({
  rows,
  loading,
  allSelected,
  someSelected,
  isSelected,
  onToggleRow,
  onToggleAll,
  onOpenDialog,
}) => (
  <TableContainer
    component={Paper}
    elevation={0}
    sx={{
      border: "1px solid rgba(0,0,0,0.08)",
      borderRadius: 2,
      width: "100%",
      overflowX: "auto",
      position: "relative",
    }}
    aria-busy={loading}
  >
    {loading && rows.length > 0 && <LoadingOverlay />}
    <Table size="small" sx={{ minWidth: 1400, width: "100%" }}>
      <ClientsTableHead
        allSelected={allSelected}
        someSelected={someSelected}
        disabled={!rows.length}
        onToggleAll={onToggleAll}
      />
      <TableBody>
        {rows.map((client) => (
          <ClientRow
            key={client.id}
            client={client}
            selected={isSelected(client.id)}
            onToggle={onToggleRow}
            onOpenDialog={onOpenDialog}
          />
        ))}
        {!rows.length && (
          <TableRow>
            <TableCell colSpan={COLUMN_COUNT} align="center" sx={{ py: 6 }}>
              {loading ? (
                <LoadingSpinner />
              ) : (
                <Typography color="text.secondary">Brak klientów dla wybranych filtrów.</Typography>
              )}
            </TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
  </TableContainer>
);

export default ClientsTable;
